// System
import { useSnackbar } from 'notistack';
import { useFormState } from 'react-hook-form';
import { BaseSyntheticEvent, useMemo } from 'react';
// Redux
import { AppDispatch } from 'src/app/store';
import { useSelector, useDispatch } from 'react-redux';
import {
  setShowAddAviatorPermissionsModal,
  setSelectedRolePermissionToDelete,
  setShowCreateAviatorPermissionForm,
  setShowSelectedRolePermissionDeleteModal,
} from '@Modal/reducers/aviatorRoles/aviatorPermissionsSlice';
// Form
import { useAddRolePermissionsForm } from '@Forms/aviatorPermissions/AddRolePermissionsFormContext';
import { useCreateAviatorPermissionForm } from '@Forms/aviatorPermissions/CreateAviatorPermissionFormContext';
// Types
import { StoreInterface } from 'src/app/types';
// Graphql
import {
  useAssignRolePermissionsMutation,
  useCreateAviatorPermissionMutation,
  useDeleteSelectedRolePermissionMutation,
} from '@Src/generated.graphql';
import { useAviatorRoleDrawer } from './useAviatorRoleDrawer';

export const useAviatorPermissions = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { selectedAviatorRoleData, handleAviatorRoleRowClick } = useAviatorRoleDrawer();
  const [assignRolePermissions] = useAssignRolePermissionsMutation();
  const [createAviatorPermission] = useCreateAviatorPermissionMutation();
  const [deleteSelectedRolePermission] = useDeleteSelectedRolePermissionMutation();

  const {
    showAddAviatorPermissionsModal,
    selectedRolePermissionToDelete,
    showCreateAviatorPermissionForm,
    showSelectedRolePermissionDeleteModal,
  } = useSelector((state: StoreInterface) => state.aviatorPermissions);

  const { enqueueSnackbar } = useSnackbar();

  const { control, handleSubmit, reset } = useCreateAviatorPermissionForm();
  const { isDirty, errors } = useFormState({ control });

  const {
    control: addRolePermissionsControl,
    handleSubmit: addRolePermissionsHandleSubmit,
    reset: addRolePermissionsReset,
  } = useAddRolePermissionsForm();
  const { isDirty: addRolePermissionsIsDirty, errors: addRolePermissionsErrors } = useFormState({
    control: addRolePermissionsControl,
  });

  const handleCloseCreateAviatorPermissionModalDialog = () => {
    dispatch(setShowCreateAviatorPermissionForm(false));
    reset();
  };

  const handleCloseAddRolePermissionsForm = () => {
    dispatch(setShowAddAviatorPermissionsModal(false));
    addRolePermissionsReset();
  };

  const handleDeleteSelectedRolePermission = async (roleId: string, permission: string) => {
    try {
      await deleteSelectedRolePermission({ variables: { input: { roleId, permission } } });
      enqueueSnackbar('Permission was successfully deleted', {
        variant: 'success',
        autoHideDuration: 2000,
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'right',
        },
      });
      dispatch(setShowSelectedRolePermissionDeleteModal(false));
    } catch (error) {
      enqueueSnackbar(error.message, {
        variant: 'error',
        autoHideDuration: 2000,
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'right',
        },
      });
    }
  };

  const handleSubmitCreateAviatorPermission = useMemo(
    () => (e?: BaseSyntheticEvent) => {
      return handleSubmit(async (formData) => {
        try {
          await createAviatorPermission({
            variables: formData,
          });
          enqueueSnackbar('Permission was successfully created', {
            variant: 'success',
            autoHideDuration: 2000,
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'right',
            },
          });
          handleCloseCreateAviatorPermissionModalDialog();
        } catch (error) {
          enqueueSnackbar(error.message, {
            variant: 'error',
            autoHideDuration: 2000,
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'right',
            },
          });
        }
      })(e);
    },
    [createAviatorPermission, reset, dispatch]
  );
  // this
  const handleSubmitAddRolePermissions = useMemo(
    () => (e?: BaseSyntheticEvent) => {
      return addRolePermissionsHandleSubmit(async (formData) => {
        try {
          await assignRolePermissions({
            variables: {
              input: { roleId: selectedAviatorRoleData?.id, permissions: formData?.permissions },
            },
          });
          enqueueSnackbar('Permissions were successfully added', {
            variant: 'success',
            autoHideDuration: 2000,
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'right',
            },
          });
          reset();
          handleCloseAddRolePermissionsForm();
          handleAviatorRoleRowClick(selectedAviatorRoleData?.id);
        } catch (error) {
          enqueueSnackbar(error.message, {
            variant: 'error',
            autoHideDuration: 2000,
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'right',
            },
          });
        }
      })(e);
    },
    []
  );

  return {
    // Store
    submittable: isDirty && Object.values(errors).length == 0,
    addRolePermissionsSubmittable:
      addRolePermissionsIsDirty && Object.values(addRolePermissionsErrors).length === 0,
    selectedRolePermissionToDelete,
    showAddAviatorPermissionsModal,
    showCreateAviatorPermissionForm,
    showSelectedRolePermissionDeleteModal,
    // Actions
    setSelectedRolePermissionToDelete,
    setShowAddAviatorPermissionsModal,
    setShowCreateAviatorPermissionForm,
    setShowSelectedRolePermissionDeleteModal,
    handleSubmitAddRolePermissions,
    handleCloseAddRolePermissionsForm,
    handleDeleteSelectedRolePermission,
    handleSubmitCreateAviatorPermission,
    handleCloseCreateAviatorPermissionModalDialog,
  };
};
